import React, { useState } from "react";
import { useRequest } from "ahooks";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useUser } from "../../contexts";
import { getRemoteConfigApi, updateProfile } from "../../api";
import AccountLayout from "../../layouts/AccountLayout";
import {
  Button,
  Flex,
  Loader,
  NumberInput,
  PasswordInput,
  Text,
  TextInput,
} from "@mantine/core";
import { RemoteConfig } from "../../interfaces";

const Profile: React.FC = () => {
  const [{ user }] = useUser();
  const { t } = useTranslation();
  const [emailChanged, setEmailChanged] = useState<boolean>(false);

  const { data, loading } = useRequest<{ data?: RemoteConfig }>(
    getRemoteConfigApi,
  );

  const updateProfileAction = useRequest((values) => updateProfile(values), {
    manual: true,
    onSuccess: (values: any) => {
      if (!values) return;

      if (user?.email !== values?.email) setEmailChanged(true);
      const form = document.getElementById("profile_form") as HTMLFormElement;
      const input1 = form.querySelector(
        'input[name="newPassword"]',
      ) as HTMLInputElement;
      const input2 = form.querySelector(
        'input[name="currentPassword"]',
      ) as HTMLInputElement;
      input1.value = "";
      input2.value = "";
    },
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();

    const email = e.target["email"].value;
    const manufacturerName = e.target["manufacturerName"].value;
    const remoteHost = e.target["remoteHost"].value;
    const remotePort = e.target["remotePort"].value;
    const remoteUsername = e.target["remoteUsername"].value;
    const remotePassword = e.target["remotePassword"].value;
    const currentPassword = e.target["currentPassword"].value;
    const newPassword = e.target["newPassword"].value;

    const remoteConfig = {
      remoteHost,
      remotePort: +remotePort,
      remoteUsername,
      remotePassword,
    };

    if (
      user?.email === email &&
      user?.name === manufacturerName &&
      data?.data &&
      JSON.stringify(remoteConfig) === JSON.stringify(data.data) &&
      !newPassword
    ) {
      toast.info("Profile has no new changes");
      return;
    }

    updateProfileAction.run({
      email,
      manufacturerName,
      remoteConfig,
      currentPassword,
      newPassword,
      user,
    });
  };

  return (
    <AccountLayout account>
      <div style={{ maxWidth: 600, margin: "auto" }}>
        <div className="text-center mb-20">
          <p style={{ fontSize: 22 }} className="f-w-500">
            {t("profile")}
          </p>
        </div>
        <form id="profile_form" className="form" onSubmit={handleSubmit}>
          <div className="form__row">
            <Text>{t("manufacturerId")}</Text>
            <TextInput
              name="manufacturerId"
              type="text"
              placeholder={t("manufacturerId")}
              defaultValue={user?.name}
              disabled
            />
          </div>
          <div className="form__row">
            <Text>{t("manufacturerName")}*</Text>
            <TextInput
              name="manufacturerName"
              type="text"
              placeholder={t("manufacturerName") + "*"}
              defaultValue={user?.manufacturerName}
              required
            />
          </div>
          <div className="form__row">
            <Text>{t("changeEmail")}*</Text>
            <TextInput
              name="email"
              type="email"
              placeholder={t("email") + "*"}
              defaultValue={user?.email}
              required
            />
            {emailChanged && (
              <p className="color-blue">({t("verifyEmailText")})</p>
            )}
          </div>

          <div className="card remote-address">
            <Text weight={600}>FTP credentials</Text>
            {!loading && (data || data === null) ? (
              <>
                <div className="form__row mt-10">
                  <Flex gap={4}>
                    <Text>{t("remoteAddress")}*</Text>
                    <Text italic>(host:port)</Text>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <TextInput
                      w="calc(100% - 120px)"
                      name="remoteHost"
                      type="text"
                      placeholder={t("remoteHost") + "*"}
                      defaultValue={data?.data?.remoteHost}
                      required
                    />
                    <Text weight={600}>:</Text>
                    <NumberInput
                      w={100}
                      name="remotePort"
                      placeholder={t("remotePort") + "*"}
                      defaultValue={data?.data?.remotePort}
                      hideControls
                      required
                    />
                  </Flex>
                </div>
                <div className="form__row mt-10">
                  <Text>{t("remoteUsername")}*</Text>
                  <TextInput
                    name="remoteUsername"
                    type="text"
                    placeholder={t("remoteUsername") + "*"}
                    defaultValue={data?.data?.remoteUsername}
                    required
                  />
                </div>
                <div className="form__row mt-10">
                  <Text>{t("remotePassword")}*</Text>
                  <PasswordInput
                    name="remotePassword"
                    placeholder={t("remotePassword") + "*"}
                    defaultValue={data?.data?.remotePassword || ""}
                    required
                  />
                </div>
              </>
            ) : (
              <Loader mt={8} size={24} />
            )}
          </div>

          <div className="form__row">
            <Text>{t("newPassword")}</Text>
            <PasswordInput name="newPassword" placeholder={t("newPassword")} />
          </div>
          <div className="form__row">
            <Text>{t("currentPassword")}*</Text>
            <PasswordInput
              name="currentPassword"
              placeholder={t("currentPassword") + "*"}
              required
            />
          </div>
          <div className="form__row">
            <Button
              disabled={updateProfileAction.loading}
              loading={updateProfileAction.loading}
              className="button form-control mt-10"
              type="submit"
              size="lg"
            >
              {t("updateProfile")}
            </Button>
          </div>
        </form>
      </div>
    </AccountLayout>
  );
};

export default Profile;
